import cx from 'classnames'
import { computed } from 'mobx'
import { observer } from 'mobx-react-lite'
import { useCallback } from 'react'

import { Button } from 'components/Button'
import { CollabItemType, CommentItem } from 'stores/DocStore.types'
import { useRootStore } from 'stores/RootStore'
import { CommentStatus } from 'types'

import { CollaborationDialogChevron } from './CollaborationDialogChevron'
import { CollaborationDialogCloseButton } from './CollaborationDialogCloseButton'
import { CollaborationDialogHead } from './CollaborationDialogHead'
import Styles from './CollaborationDialogSelectedCommentHead.module.scss'
import { useCollaborationDialog } from './CollaborationDialogStore'
import { ReactComponent as ArrowDownIcon } from './icons/arrowDown.svg'
import { ReactComponent as ArrowUpIcon } from './icons/arrowUp.svg'

export const CollaborationDialogSelectedCommentHead = observer(function CollaborationDialogSelectedCommentHead() {
  const collaborationDialogStore = useCollaborationDialog()

  return (
    <CollaborationDialogHead>
      <div className={Styles.left}>
        <Button
          preventFocusOnPress
          variant="none"
          size="md"
          onClick={() => {
            collaborationDialogStore.toggleViewAll()
          }}
        >
          {collaborationDialogStore.viewAll ? 'Just selected' : 'View all'}
        </Button>
      </div>
      <div className={Styles.middle}>
        <CommentNavigation />
      </div>
      <div className={Styles.right}>
        <CollaborationDialogChevron />
        <CollaborationDialogCloseButton />
      </div>
    </CollaborationDialogHead>
  )
})

const CommentNavigation = observer(function CommentNavigation() {
  const rootStore = useRootStore()
  const collaborationDialogStore = useCollaborationDialog()
  // TODO: Move these computed features to the store, reaction or something or the sorts
  const noSelection = computed(() => !rootStore.comments.selected?.id).get()
  const onlyComments = computed(() =>
    rootStore.docStore?.orderedFilteredCollabItems?.filter((e): e is CommentItem => e.type === CollabItemType.Comment),
  ).get()
  const selectedNotInFilter = computed(
    () => !onlyComments?.some((e): e is CommentItem => rootStore.comments.selected?.id === e.item.id),
  ).get()
  const commentIndex =
    computed(() =>
      onlyComments?.findIndex(
        (e): e is CommentItem =>
          collaborationDialogStore.viewAll ||
          noSelection ||
          selectedNotInFilter ||
          rootStore.comments.selected?.id === e.item.id,
      ),
    ).get() ?? 0
  const prevCommentId = computed(() => {
    if (commentIndex > 0) return onlyComments?.[commentIndex - 1].item.id
  }).get()
  const nextCommentId = computed(() => {
    if (onlyComments && commentIndex < onlyComments.length - 1) return onlyComments?.[commentIndex + 1].item.id
  }).get()
  // NOTICE: rootStore.comments.selected?.status lags a little behind the real status, that causes the UI to misbehave
  const isResolved = computed(
    () =>
      rootStore.comments.selected &&
      rootStore.comments.get(rootStore.comments.selected.id)?.status === CommentStatus.Resolved,
  ).get()
  const goToComment = useCallback((commentId: number) => () => rootStore.comments.select(commentId), [rootStore])

  if (isResolved) return <span className={Styles.resolvedTag}>(Resolved)</span>

  return (
    <>
      <Button
        preventFocusOnPress
        frontIcon={ArrowUpIcon}
        variant="none"
        size="sm"
        className={cx({ [Styles.invisibleBtn]: !prevCommentId })}
        onClick={prevCommentId ? goToComment(prevCommentId) : undefined}
      />

      <span className={Styles.commentCounter}>
        {commentIndex + 1} of {collaborationDialogStore.filteredCommentsLength}
      </span>

      <Button
        preventFocusOnPress
        frontIcon={ArrowDownIcon}
        variant="none"
        size="sm"
        className={cx({ [Styles.invisibleBtn]: !nextCommentId })}
        onClick={nextCommentId ? goToComment(nextCommentId) : undefined}
      />
    </>
  )
})
